// /teams/:teamId/projects/:projectId/flows — list flows with status badges
// driven by latest run + outstanding drift counts.
//
// Per PRODUCT.md: the CLI is the hero, the dashboard is the audience. This
// page does not author. It shows existing flows, points users at
// `cairn record` to capture new ones, and offers a schema-validated
// "Import YAML" affordance as a secondary fallback for hand-authored files.

// Import from the browser-safe `/parse` subpath: the package root re-exports
// writeAtomic (node:fs/node:path), which Vite cannot bundle for the browser.
import { validateFlow } from '@cairntools/schema/parse'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '../../components/dashboard/PageHeader.jsx'
import { Button } from '../../components/ui/button.jsx'
import {
  useDriftCountsByFlow,
  useFlows,
  useLatestRunsByFlow,
  useProject,
} from '../../lib/data-hooks.js'
import { createFlow } from '../../lib/mutations.js'

function StatusBadge({ run, driftCount }) {
  if (driftCount > 0) {
    return (
      <span
        title={`${driftCount} unresolved drift`}
        className="inline-flex items-center gap-1.5 rounded-sm bg-[var(--color-running)]/15 px-2 py-0.5 text-xs font-medium text-[var(--color-running)]"
      >
        <span aria-hidden="true">◆</span>
        drift · {driftCount}
      </span>
    )
  }
  if (!run) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-sm bg-[var(--color-sandstone)] px-2 py-0.5 text-xs text-[var(--color-schist)]">
        <span aria-hidden="true">○</span>
        never run
      </span>
    )
  }
  if (run.status === 'passed') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-sm bg-[var(--color-passed)]/15 px-2 py-0.5 text-xs font-medium text-[var(--color-passed)]">
        <span aria-hidden="true">✓</span>
        passed
      </span>
    )
  }
  if (run.status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-sm bg-[var(--color-failed)]/15 px-2 py-0.5 text-xs font-medium text-[var(--color-failed)]">
        <span aria-hidden="true">✕</span>
        failed
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-sm bg-[var(--color-running)]/15 px-2 py-0.5 text-xs font-medium text-[var(--color-running)]">
      <span aria-hidden="true">◌</span>
      {run.status}
    </span>
  )
}

const SORT_OPTIONS = [
  { id: 'name', label: 'Name' },
  { id: 'updated', label: 'Last updated' },
]

function sortFlows(flows, sortBy) {
  const out = [...flows]
  if (sortBy === 'name') {
    out.sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id))
  } else if (sortBy === 'updated') {
    out.sort((a, b) => {
      const av = a.updatedAt?.toMillis?.() ?? 0
      const bv = b.updatedAt?.toMillis?.() ?? 0
      return bv - av
    })
  }
  return out
}

function CopyableCommand({ command }) {
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(command)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard API unavailable — fall through; the command is still
      // selectable as text.
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={`Copy command: ${command}`}
      className="group flex items-center gap-3 rounded-md border border-[var(--color-sandstone)] bg-white px-4 py-2.5 text-left font-mono text-sm text-[var(--color-basalt)] transition-colors hover:border-[var(--color-terracotta)]/60"
    >
      <span aria-hidden="true" className="text-[var(--color-schist)]">
        $
      </span>
      <span className="flex-1">{command}</span>
      <span className="text-[10px] uppercase tracking-wider text-[var(--color-schist)] group-hover:text-[var(--color-terracotta)]">
        {copied ? 'Copied' : 'Copy'}
      </span>
    </button>
  )
}

export function FlowsList() {
  const { teamId, projectId } = useParams()
  const navigate = useNavigate()
  const { data: project } = useProject(teamId, projectId)
  const { data: flows, loading } = useFlows(teamId, projectId)
  const { data: latestRuns } = useLatestRunsByFlow(teamId, projectId)
  const { data: driftCounts } = useDriftCountsByFlow(teamId, projectId)

  const [sortBy, setSortBy] = useState('name')
  const [importing, setImporting] = useState(false)
  const [name, setName] = useState('')
  const [yaml, setYaml] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState(null)

  async function handleFileChosen(e) {
    const file = e.target.files?.[0]
    if (!file) return
    const text = await file.text()
    setYaml(text)
    if (!name) {
      // Drop the extension and any path: login.yaml → login
      setName(file.name.replace(/\.ya?ml$/i, '').replace(/.*\//, ''))
    }
  }

  async function handleImport(e) {
    e.preventDefault()
    setFormError(null)
    const result = validateFlow(yaml)
    if (!result.ok) {
      const first = result.errors[0]
      const path =
        Array.isArray(first.path) && first.path.length > 0 ? `${first.path.join('.')}: ` : ''
      setFormError(`${path}${first.message}`)
      return
    }
    setSubmitting(true)
    try {
      const flowId = await createFlow({
        teamId,
        projectId,
        name: name.trim(),
        yaml,
      })
      navigate(`/teams/${teamId}/projects/${projectId}/flows/${flowId}`)
    } catch (err) {
      setFormError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  function cancelImport() {
    setImporting(false)
    setName('')
    setYaml('')
    setFormError(null)
  }

  const sorted = sortFlows(flows, sortBy)

  return (
    <>
      <PageHeader
        breadcrumb={[
          { label: 'Teams', to: '/teams' },
          { label: teamId, to: `/teams/${teamId}` },
          { label: project?.name ?? projectId, to: `/teams/${teamId}/projects/${projectId}` },
          { label: 'Flows' },
        ]}
        title="Flows"
        subhead="Flow graphs versioned in this project"
        actions={
          <>
            <label className="inline-flex items-center gap-2 text-xs text-[var(--color-schist)]">
              Sort
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="rounded-md border border-[var(--color-sandstone)] bg-white px-2 py-1 text-xs"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            {!importing && (
              <Button onClick={() => setImporting(true)} variant="ghost" size="sm">
                Import YAML
              </Button>
            )}
          </>
        }
      />

      <div className="mx-auto max-w-5xl px-6 py-8">
        {importing && (
          <form
            onSubmit={handleImport}
            className="mb-8 rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)] p-5"
          >
            <h2 className="mb-1 text-sm font-medium text-[var(--color-basalt)]">
              Import an existing YAML
            </h2>
            <p className="mb-4 text-xs text-[var(--color-schist)]">
              Authoring happens in the CLI (<code className="font-mono">cairn record</code>). Use
              this when you already have a flow YAML on disk and need to land it in this project.
              Schema-validated before write.
            </p>
            <label className="block">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
                Flow name
              </span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="login"
                className="w-full rounded-md border border-[var(--color-sandstone)] bg-white px-3 py-2 text-sm focus:outline-none focus-visible:border-[var(--color-terracotta)]"
              />
            </label>
            <label className="mt-4 block">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
                YAML file
              </span>
              <input
                type="file"
                accept=".yaml,.yml,text/yaml"
                onChange={handleFileChosen}
                className="block w-full text-xs text-[var(--color-slate)] file:mr-3 file:rounded-md file:border file:border-[var(--color-sandstone)] file:bg-white file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-[var(--color-basalt)] hover:file:border-[var(--color-terracotta)]/60"
              />
            </label>
            <label className="mt-4 block">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
                YAML
              </span>
              <textarea
                value={yaml}
                onChange={(e) => setYaml(e.target.value)}
                required
                rows={14}
                placeholder="apiVersion: cairntools.com/v1alpha1&#10;kind: Flow&#10;…"
                className="w-full rounded-md border border-[var(--color-sandstone)] bg-white px-3 py-2 font-mono text-xs focus:outline-none focus-visible:border-[var(--color-terracotta)]"
              />
            </label>
            {formError && (
              <p className="mt-2 text-xs text-[var(--color-failed)]" role="alert">
                {formError}
              </p>
            )}
            <div className="mt-4 flex gap-2">
              <Button
                type="submit"
                disabled={submitting || !name.trim() || !yaml.trim()}
                variant="primary"
                size="sm"
              >
                {submitting ? 'Importing…' : 'Import'}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={cancelImport}>
                Cancel
              </Button>
            </div>
          </form>
        )}

        {loading && <p className="text-sm text-[var(--color-schist)]">Loading flows…</p>}
        {!loading && flows.length === 0 && !importing && (
          <div className="rounded-md border border-dashed border-[var(--color-sandstone)] bg-[var(--color-sandstone)]/30 p-8">
            <p className="mb-1 text-sm font-medium text-[var(--color-basalt)]">No flows yet.</p>
            <p className="mb-5 text-sm text-[var(--color-slate)]">
              Record one in your terminal — Cairn captures the walkthrough and writes the YAML to{' '}
              <code className="font-mono text-xs">.graphs/</code>.
            </p>
            <CopyableCommand command="cairn record <flow-name>" />
            <p className="mt-5 text-xs text-[var(--color-schist)]">
              Already have a YAML on disk?{' '}
              <button
                type="button"
                onClick={() => setImporting(true)}
                className="text-[var(--color-terracotta)] underline-offset-4 hover:underline"
              >
                Import YAML
              </button>
              .
            </p>
          </div>
        )}

        {sorted.length > 0 && (
          <ul className="divide-y divide-[var(--color-sandstone)] rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)]">
            {sorted.map((f) => (
              <li key={f.id}>
                <Link
                  to={`/teams/${teamId}/projects/${projectId}/flows/${f.id}`}
                  className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-[var(--color-sandstone)]/40"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-[var(--color-basalt)]">{f.name ?? f.id}</div>
                    <div className="truncate font-mono text-xs text-[var(--color-schist)]">
                      {f.id}
                    </div>
                  </div>
                  <StatusBadge run={latestRuns.get(f.id)} driftCount={driftCounts.get(f.id) ?? 0} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  )
}
